import React, { useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Col,
  Empty,
  Row,
  Segmented,
  Skeleton,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { toIsoDate } from '../../utils/dateHelpers';
import { formatCurrencyLocalized, formatUnitCost } from '../../utils/currencyHelpers';
import MobileDateRangePicker from '../common/MobileDateRangePickerV2';
import {
  useGetInventoryCostSummaryQuery,
  useGetProductMarginsQuery,
  useGetConsumptionReportQuery,
  useGetWasteReportQuery,
  useGetInventoryValuationQuery,
} from '../../services/inventoryApi';
import type {
  ProductMargin,
  ConsumptionByItem,
  WasteByItem,
  ValuationItem,
} from '../../types/inventory';
import { formatStock } from '../../utils/unitHelpers';

const { Text } = Typography;

type QuickRange = 'today' | 'week' | 'month' | 'custom';
type Section = 'margins' | 'consumption' | 'waste' | 'valuation';

const CostsTab: React.FC = () => {
  const { isMobile } = useResponsive();
  const { timeZone, startOfDay, startOfWeek, startOfMonth } = useRestaurantTimeZone();

  const [quickRange, setQuickRange] = useState<QuickRange>('month');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [section, setSection] = useState<Section>('margins');

  // Anclas en la TZ del restaurante (no la del navegador) — misma regla que el P&L
  const [from, to] = useMemo((): [Dayjs, Dayjs] => {
    const now = dayjs().tz(timeZone);
    switch (quickRange) {
      case 'today':
        return [startOfDay(), now];
      case 'week':
        return [startOfWeek(), now];
      case 'month':
        return [startOfMonth(), now];
      case 'custom':
        return customRange && customRange[0] && customRange[1]
          ? [customRange[0], customRange[1]]
          : [startOfMonth(), now];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickRange, customRange, timeZone]);

  const fromStr = toIsoDate(from);
  const toStr = toIsoDate(to);
  const rangeArg = { from: fromStr, to: toStr };

  const { data: summary, isFetching: loadingSummary, isError: summaryError } =
    useGetInventoryCostSummaryQuery(rangeArg);
  const { data: margins, isFetching: loadingMargins } = useGetProductMarginsQuery(rangeArg, {
    skip: section !== 'margins',
  });
  const { data: consumption, isFetching: loadingConsumption } = useGetConsumptionReportQuery(rangeArg, {
    skip: section !== 'consumption',
  });
  const { data: waste, isFetching: loadingWaste } = useGetWasteReportQuery(rangeArg, {
    skip: section !== 'waste',
  });
  const { data: valuation, isFetching: loadingValuation } = useGetInventoryValuationQuery(undefined, {
    skip: section !== 'valuation',
  });

  const money = (n: number) => formatCurrencyLocalized(n, 'MXN');
  const pct = (n: number) => `${n.toFixed(1)}%`;

  // ===== Columnas =====

  const marginColumns: ColumnsType<ProductMargin> = [
    {
      title: 'Producto',
      dataIndex: 'productName',
      key: 'productName',
      render: (name: string, record) => (
        <span>
          <Text strong style={{ fontSize: 13 }}>{name}</Text>
          {!record.hasRecipe && (
            <Tooltip title="Sin receta ni insumo vinculado: el costo no se puede calcular">
              <Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>Sin receta</Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'Categoría',
      dataIndex: 'categoryName',
      key: 'categoryName',
      responsive: ['md'],
      render: (t: string) => <Text type="secondary" style={{ fontSize: 12 }}>{t}</Text>,
    },
    {
      title: 'Vendidos',
      dataIndex: 'unitsSold',
      key: 'unitsSold',
      align: 'right',
      sorter: (a, b) => a.unitsSold - b.unitsSold,
    },
    {
      title: 'Ingresos',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      sorter: (a, b) => a.revenue - b.revenue,
      defaultSortOrder: 'descend',
      render: (v: number) => money(v),
    },
    {
      title: 'Costo est.',
      dataIndex: 'estimatedCost',
      key: 'estimatedCost',
      align: 'right',
      sorter: (a, b) => a.estimatedCost - b.estimatedCost,
      render: (v: number, r) => (r.hasRecipe ? money(v) : '—'),
    },
    {
      title: 'Margen',
      dataIndex: 'marginAmount',
      key: 'marginAmount',
      align: 'right',
      sorter: (a, b) => a.marginAmount - b.marginAmount,
      render: (v: number, r) => (r.hasRecipe ? money(v) : '—'),
    },
    {
      title: 'Food cost',
      dataIndex: 'foodCostPercentage',
      key: 'foodCostPercentage',
      align: 'right',
      sorter: (a, b) => a.foodCostPercentage - b.foodCostPercentage,
      render: (v: number, r) =>
        r.hasRecipe ? (
          <Text style={{ color: v > 40 ? 'var(--color-error-text)' : undefined }}>{pct(v)}</Text>
        ) : (
          '—'
        ),
    },
  ];

  const consumptionColumns: ColumnsType<ConsumptionByItem> = [
    {
      title: 'Insumo',
      dataIndex: 'itemName',
      key: 'itemName',
      render: (t: string, r) => (
        <span>
          <Text strong style={{ fontSize: 13 }}>{t}</Text>
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>{r.categoryName}</Text>
        </span>
      ),
    },
    {
      title: 'Vendido',
      key: 'sold',
      align: 'right',
      sorter: (a, b) => a.soldCost - b.soldCost,
      render: (_, r) => (
        <span>
          {r.soldQuantity.toFixed(2)} {r.unit}
          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{money(r.soldCost)}</Text>
        </span>
      ),
    },
    {
      title: 'Merma',
      key: 'waste',
      align: 'right',
      sorter: (a, b) => a.wasteCost - b.wasteCost,
      render: (_, r) =>
        r.wasteQuantity > 0 ? (
          <span>
            {r.wasteQuantity.toFixed(2)} {r.unit}
            <Text style={{ fontSize: 11, display: 'block', color: 'var(--color-error-text)' }}>
              {money(r.wasteCost)}
            </Text>
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: 'Consumo interno',
      key: 'internal',
      align: 'right',
      responsive: ['md'],
      sorter: (a, b) => a.internalCost - b.internalCost,
      render: (_, r) =>
        r.internalQuantity > 0 ? (
          <span>
            {r.internalQuantity.toFixed(2)} {r.unit}
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{money(r.internalCost)}</Text>
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: 'Costo total',
      dataIndex: 'totalCost',
      key: 'totalCost',
      align: 'right',
      sorter: (a, b) => a.totalCost - b.totalCost,
      defaultSortOrder: 'descend',
      render: (v: number) => <Text strong>{money(v)}</Text>,
    },
  ];

  const wasteItemColumns: ColumnsType<WasteByItem> = [
    { title: 'Insumo', dataIndex: 'itemName', key: 'itemName' },
    {
      title: 'Cantidad',
      key: 'quantity',
      align: 'right',
      render: (_, r) => `${r.quantity.toFixed(2)} ${r.unit}`,
    },
    {
      title: 'Pérdida',
      dataIndex: 'cost',
      key: 'cost',
      align: 'right',
      sorter: (a, b) => a.cost - b.cost,
      defaultSortOrder: 'descend',
      render: (v: number) => <Text style={{ color: 'var(--color-error-text)' }}>{money(v)}</Text>,
    },
  ];

  const valuationColumns: ColumnsType<ValuationItem> = [
    {
      title: 'Insumo',
      dataIndex: 'itemName',
      key: 'itemName',
      render: (t: string, r) => (
        <span>
          <Text strong style={{ fontSize: 13 }}>{t}</Text>
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>{r.categoryName}</Text>
        </span>
      ),
    },
    {
      title: 'Stock',
      key: 'stock',
      align: 'right',
      render: (_, r) => `${formatStock(r.currentStock)} ${r.unit}`,
    },
    {
      title: 'Costo unitario',
      dataIndex: 'costPerUnit',
      key: 'costPerUnit',
      align: 'right',
      responsive: ['md'],
      render: (v: number) => formatUnitCost(v),
    },
    {
      title: 'Valor',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      sorter: (a, b) => a.value - b.value,
      defaultSortOrder: 'descend',
      render: (v: number) => (
        <Text strong style={{ color: v < 0 ? 'var(--color-error-text)' : undefined }}>{money(v)}</Text>
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? 0 : '16px 0' }}>
      {/* Periodo */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <Segmented
          value={quickRange}
          onChange={(v) => setQuickRange(v as QuickRange)}
          options={[
            { label: 'Hoy', value: 'today' },
            { label: 'Semana', value: 'week' },
            { label: 'Mes', value: 'month' },
            { label: 'Personalizado', value: 'custom' },
          ]}
        />
        {quickRange === 'custom' && (
          <MobileDateRangePicker
            value={customRange}
            onChange={(v) => setCustomRange(v && v[0] && v[1] ? [v[0], v[1]] : null)}
            format="DD/MM/YYYY"
            allowClear={false}
            style={{ width: isMobile ? '100%' : 260 }}
          />
        )}
      </div>

      {summaryError && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el resumen de costos."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* KPIs del periodo */}
      {loadingSummary && !summary ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : summary ? (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title={
                  <span>
                    Food cost real{' '}
                    <Tooltip title="Costo de insumos descontados por ventas ÷ ventas netas del periodo. Usa el costo registrado al momento de cada venta.">
                      <InfoCircleOutlined style={{ fontSize: 12 }} />
                    </Tooltip>
                  </span>
                }
                value={summary.foodCostPercentage}
                precision={1}
                suffix="%"
                valueStyle={{
                  color:
                    summary.foodCostPercentage > 40
                      ? 'var(--color-error-text)'
                      : 'var(--color-success-text)',
                }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {money(summary.costOfGoodsSold)} de {money(summary.netRevenue)} vendidos
              </Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Utilidad bruta estimada" value={summary.estimatedGrossProfit} precision={2} prefix="$" />
              <Text type="secondary" style={{ fontSize: 11 }}>ventas netas − costo de lo vendido</Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Pérdida por mermas"
                value={summary.wasteCost}
                precision={2}
                prefix="$"
                valueStyle={summary.wasteCost > 0 ? { color: 'var(--color-error-text)' } : undefined}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                consumo interno: {money(summary.internalConsumptionCost)}
              </Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Compras del periodo" value={summary.purchasesTotal} precision={2} prefix="$" />
              <Text type="secondary" style={{ fontSize: 11 }}>recepciones de órdenes de compra</Text>
            </Card>
          </Col>
        </Row>
      ) : null}

      {/* Secciones */}
      <Segmented
        value={section}
        onChange={(v) => setSection(v as Section)}
        options={[
          { label: 'Márgenes por producto', value: 'margins' },
          { label: 'Consumo de insumos', value: 'consumption' },
          { label: 'Mermas', value: 'waste' },
          { label: 'Valor del inventario', value: 'valuation' },
        ]}
        style={{ marginBottom: 12, maxWidth: '100%', overflowX: 'auto' }}
      />

      {section === 'margins' && (
        <>
          {margins && margins.productsWithoutRecipe > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${margins.productsWithoutRecipe} producto(s) vendidos sin receta ni insumo vinculado — su costo no entra al cálculo.`}
              style={{ marginBottom: 12 }}
            />
          )}
          <Table
            columns={marginColumns}
            dataSource={margins?.products || []}
            loading={loadingMargins}
            rowKey="productId"
            size="small"
            scroll={{ x: 800 }}
            pagination={{ pageSize: 20, showTotal: (t) => `${t} productos` }}
            locale={{ emptyText: <Empty description="Sin ventas en el periodo" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        </>
      )}

      {section === 'consumption' && (
        <Table
          columns={consumptionColumns}
          dataSource={consumption?.items || []}
          loading={loadingConsumption}
          rowKey="inventoryItemId"
          size="small"
          scroll={{ x: 700 }}
          pagination={{ pageSize: 20, showTotal: (t) => `${t} insumos` }}
          locale={{ emptyText: <Empty description="Sin consumo en el periodo" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      )}

      {section === 'waste' && (
        <>
          {waste && waste.byReason.length > 0 && (
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
              {waste.byReason.map((r) => (
                <Col xs={12} md={6} key={r.reason}>
                  <Card size="small">
                    <Statistic
                      title={r.reason}
                      value={r.cost}
                      precision={2}
                      prefix="$"
                      valueStyle={{ fontSize: 18, color: 'var(--color-error-text)' }}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>{r.count} registro(s)</Text>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
          <Table
            columns={wasteItemColumns}
            dataSource={waste?.byItem || []}
            loading={loadingWaste}
            rowKey="inventoryItemId"
            size="small"
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: <Empty description="Sin mermas registradas en el periodo" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        </>
      )}

      {section === 'valuation' && (
        <>
          {valuation && (
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
              <Col xs={24} md={8}>
                <Card size="small">
                  <Statistic title="Valor total del inventario" value={valuation.totalValue} precision={2} prefix="$" />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    stock actual × costo capturado por insumo
                  </Text>
                </Card>
              </Col>
              {valuation.byCategory.slice(0, 4).map((c) => (
                <Col xs={12} md={4} key={c.categoryId}>
                  <Card size="small">
                    <Statistic title={c.categoryName} value={c.value} precision={2} prefix="$" valueStyle={{ fontSize: 16 }} />
                    <Text type="secondary" style={{ fontSize: 11 }}>{c.itemCount} insumo(s)</Text>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
          <Table
            columns={valuationColumns}
            dataSource={valuation?.items || []}
            loading={loadingValuation}
            rowKey="inventoryItemId"
            size="small"
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: <Empty description="Sin insumos activos" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        </>
      )}

      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
        Los costos usan el costo por unidad capturado en cada insumo; mantenlo al día para que estos
        reportes reflejen la realidad.
      </div>
    </div>
  );
};

export default CostsTab;
